import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import { BrandLogo } from './BrandLogo';
import { BottomTabBar } from './BottomTabBar';
import { TopNav } from './TopNav';
import { MoreSheet } from './MoreSheet';

export const IconButton = ({ children, className = '', ...props }) => (
  <button
    type="button"
    className={`tap-none flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.04] text-[#a1a1aa] transition-colors hover:text-white active:scale-95 ${className}`}
    {...props}
  >
    {children}
  </button>
);

export const AppShell = ({
  accent,
  accentFg = 'text-black',
  tabs,
  mobileTabValues,
  activeTab,
  onTabChange,
  headerRight,
  headerLeft,
  pageTitle,
  pageSubtitle,
  children,
}) => {
  const isDesktop = useIsDesktop();
  const [moreOpen, setMoreOpen] = useState(false);

  const desktopTabs = tabs.filter((t) => t.desktop !== false);
  const barTabs = mobileTabValues ? tabs.filter((t) => mobileTabValues.includes(t.value)) : tabs;
  const moreTabs = tabs.filter((t) => !barTabs.includes(t) && !t.action);

  return (
    <div className="min-h-screen bg-[#09090b] text-[#fafafa]">
      <header
        className="sticky top-0 z-40 pt-safe border-b border-white/[0.06]"
        style={{ backgroundColor: 'rgba(9,9,11,0.85)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
      >
        <div className="mx-auto flex h-14 w-full max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">{headerLeft || <BrandLogo size="sm" />}</div>
          <div className="flex items-center gap-2">{headerRight}</div>
        </div>
        {isDesktop && (
          <div className="mx-auto w-full max-w-6xl px-6 pb-4">
            <TopNav tabs={desktopTabs} activeTab={activeTab} onSelect={onTabChange} accent={accent} accentFg={accentFg} />
          </div>
        )}
      </header>

      <main className="mx-auto w-full max-w-6xl px-4 pt-5 pb-28 sm:px-6 md:pt-8 md:pb-12">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >
            {pageTitle && (
              <div className="mb-5 md:mb-8">
                <h1 className="font-heading text-2xl font-semibold tracking-tight text-white sm:text-3xl" data-testid="page-title">
                  {pageTitle}
                </h1>
                {pageSubtitle && <p className="mt-1 text-sm text-[#a1a1aa]">{pageSubtitle}</p>}
              </div>
            )}
            {children}
          </motion.div>
        </AnimatePresence>
      </main>

      {!isDesktop && (
        <>
          <BottomTabBar
            tabs={barTabs}
            moreTabs={moreTabs}
            activeTab={activeTab}
            onSelect={onTabChange}
            onMore={() => setMoreOpen(true)}
            accent={accent}
            accentFg={accentFg}
          />
          <MoreSheet
            open={moreOpen}
            onClose={() => setMoreOpen(false)}
            tabs={moreTabs}
            activeTab={activeTab}
            onSelect={onTabChange}
            accent={accent}
          />
        </>
      )}
    </div>
  );
};
